import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { applyTheme, effectiveTheme, getStoredTheme, setStoredTheme, THEME_STORAGE_KEY, useTheme } from './theme';
import { ThemeName } from './palette';

/**
 * Theme step 2: keeps <html data-theme> in line with the SERVER role (AuthContext ← /api/auth/me). Graphite is honoured
 * for every signed-in role (owner 2026-10-06); light only for admin — anyone else is forced dark if `saa_theme` says "light" or
 * "graphite" (the pre-paint script in
 * public/index.html already applied it only for a cached admin role — this re-decides once the server answers, and
 * again whenever the role changes, e.g. sign-out).
 */
export function ThemeGate(): null {
  const { role } = useAuth();
  const { pathname } = useLocation();
  useEffect(() => {
    applyTheme(effectiveTheme(role, getStoredTheme(), pathname));
    // Another tab of the same admin switched the theme.
    const onStorage = (e: StorageEvent) => { if (e.key === THEME_STORAGE_KEY) applyTheme(effectiveTheme(role)); };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [role, pathname]);
  return null;
}

/**
 * Display theme control (Plan & Access): Dark / Graphite for every signed-in user, plus Light for admin. The role is the
 * server-authoritative one from AuthContext, never localStorage.
 */
export function ThemeSwitch({ style }: { style?: React.CSSProperties }): JSX.Element | null {
  const { role, loading } = useAuth();
  const active = useTheme();
  const [stored, setStored] = useState<ThemeName>(() => getStoredTheme());
  const choose = useCallback((t: ThemeName) => {
    setStoredTheme(t);
    setStored(t);
    applyTheme(effectiveTheme(role, t)); // on a gateway page (Plan & Access) this stays dark; the terminal pages follow the choice
  }, [role]);
  if (loading || !role) return null;
  const isAdmin = role === 'admin';
  const btn = (t: ThemeName, label: string) => {
    const on = stored === t && active === t;
    return (
      <button
        type="button"
        role="radio"
        aria-checked={on}
        onClick={() => choose(t)}
        data-testid={`theme-${t}`}
        style={{
          padding: '5px 12px', fontSize: 11, letterSpacing: '0.16em', textTransform: 'uppercase', cursor: 'pointer',
          border: 'none', fontFamily: "'JetBrains Mono', ui-monospace, monospace",
          background: on ? 'rgb(var(--c-gold-rgb) / 0.14)' : 'transparent',
          color: on ? 'var(--c-gold)' : 'var(--c-text-body)',
        }}
      >
        {label}
      </button>
    );
  };
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', ...style }} data-testid="theme-switch">
      <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 10.5, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--c-text-body)' }}>
        {isAdmin ? 'Display theme · terminal pages only · light is an admin preview' : 'Display theme · terminal pages'}
      </span>
      <span role="radiogroup" aria-label="Display theme" style={{ display: 'inline-flex', border: '1px solid rgb(var(--c-border-rgb) / 0.9)', borderRadius: 2 }}>
        {btn('dark', 'Dark')}
        <span aria-hidden style={{ width: 1, background: 'rgb(var(--c-border-rgb) / 0.9)' }} />
        {isAdmin && btn('light', 'Light')}
        {isAdmin && <span aria-hidden style={{ width: 1, background: 'rgb(var(--c-border-rgb) / 0.9)' }} />}
        {btn('graphite', 'Graphite')}
      </span>
    </div>
  );
}
